import {
  endOfDay,
  format,
  formatDistanceToNow,
  isBefore,
  isToday,
  isValid,
  isYesterday,
  startOfDay,
  startOfToday,
} from "date-fns";

import { tz } from "@date-fns/tz";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

const UTC = tz("UTC");

export const parseUtcDate = (input: Date | number | string): Date => {
  if (input instanceof Date) return input;

  if (typeof input === "number") return new Date(input);

  const isOffsetLessDateTime =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(input) &&
    !/[zZ]|[+-]\d{2}:?\d{2}$/.test(input);

  return new Date(isOffsetLessDateTime ? `${input}Z` : input);
};

export enum DateFormatString {
  ISO = "yyyy-MM-dd",
  ISO_DATETIME = "yyyy-MM-dd'T'HH:mm:ss",
  ISO_DATE_DOT = "yyyy.MM.dd",
  ISO_DATE_TIME_DOT = "yyyy.MM.dd - HH:mm",
  MONTH_YEAR = "MM/yyyy",
  US_DATE_SLASH = "MM/dd/yyyy",
  UK_DATE_SLASH = "dd/MM/yyyy",
  US_DATE_TIME_DOT = "MM.dd.yyyy, hh:mm",
  UK_DATE_TIME_DOT = "dd.MM.yyyy, hh:mm",
  US_DATE_DOT = "MM.dd.yyyy",
  UK_DATE_DOT = "dd.MM.yyyy",
  US_DATE_WITH_TIME_SLASH = "MM/dd/yyyy, hh:mm a",
  UK_DATE_WITH_TIME_SLASH = "dd/MM/yyyy, hh:mm a",
  UK_WITH_TIME_AM_PM_DOT = "dd.MM.yyyy hh:mm a",
  US_WITH_TIME_AM_PM_DOT = "MM.dd.yyyy hh:mm a",
  US_DATE_SIMPLE = "MMM dd yyyy",
  US_DATE_WITH_COMMA = "MMM d, yyyy",
  UK_DATE_SIMPLE = "dd MMM yyyy",
  US_TIME = "hh:mm a",
}

export const useDateUtils = () => {
  /**
   * Formats a date into a human-readable relative string. If the date is today,
   * it returns a string formatted as 'Today at [time]'. If the date was yesterday,
   * it returns 'Yesterday at [time]'. For all other dates, it returns a string
   * representing the distance to the current date with an appropriate suffix, such
   * as '3 days ago' or 'in 2 months'.
   *
   * @param {Date} date - The date to format relative to the current date.
   * @returns {string} A human-readable relative date string.
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  function customFormatRelativeDate(date: string): string {
    const dateObj = parseUtcDate(date);

    if (!isValid(dateObj)) {
      return "-";
    }

    const time = format(dateObj, "p");

    if (isToday(dateObj)) {
      return m.dates_todayFormatted({ date: format(dateObj, "hh:mm a") });
    } else if (isYesterday(dateObj)) {
      return m.dates_yesterdayFormatted({
        date: format(dateObj, "hh:mm a"),
      });
    } else {
      return `${format(dateObj, DateFormatString.US_DATE_SIMPLE)}, ${time}`;
    }
  }

  return { customFormatRelativeDate };
};

/**
 * Formats a date to a string with the month abbreviated and the day as an ordinal number (e.g., 'Jan 5th').
 *
 * @param {Date} date - The date to be formatted.
 * @returns {string} - The formatted date string.
 */
export function formatDateToMonthDayOrdinal(date: string): string {
  const dateObj = parseUtcDate(date);

  if (!isValid(dateObj)) {
    return "-";
  }

  // Get the day of the month
  const dayOfMonth = dateObj.getUTCDate();

  // Define an array of ordinal indicators
  const ordinalIndicators = ["th", "st", "nd", "rd"];

  // Calculate the correct ordinal indicator
  const ordinal =
    ordinalIndicators[
      (dayOfMonth > 3 && dayOfMonth < 21) || dayOfMonth % 10 > 3
        ? 0
        : dayOfMonth % 10
    ];

  // Format the month and append the day with the ordinal indicator
  return `${format(dateObj, "MMM", { in: UTC })} ${dayOfMonth}${ordinal}`;
}

export function formatOnlyTime(isoStringDate = ""): string {
  return formatDate(parseUtcDate(isoStringDate), false, false, true);
}

export function formatDate(
  newDate: Date,
  onlyDate?: boolean,
  onlyMonth?: boolean,
  onlyTime?: boolean,
) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (!isValid(newDate)) {
    return "-";
  }

  const d = newDate;
  const isDateOnly = !isFalsy(onlyDate) || !isFalsy(onlyMonth);
  const year = isDateOnly ? d.getUTCFullYear() : d.getFullYear();
  const date = isDateOnly ? d.getUTCDate() : d.getDate();
  const monthName = months[isDateOnly ? d.getUTCMonth() : d.getMonth()];

  const time = d.toLocaleTimeString([], {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
  });

  if (isTruthy(onlyTime)) {
    return time;
  }

  if (!isFalsy(onlyMonth)) {
    return `${monthName} ${year}`;
  }

  return !isFalsy(onlyDate)
    ? `${date} ${monthName} ${year}`
    : `${date} ${monthName} ${year}, ${time}`;
}

/**
 * Formats a given ISO date string into a short, human-readable format.
 * If withTime is true, the format will be "dd/MM/yyyy, hh/mm".
 * If withTime is false or undefined, the format will be "dd/MM/yyyy".
 *
 * @param isoDateString - The ISO date string to format.
 * @param dateFormatString - The format string to use for formatting the date
 * @returns A human-readable date string in the format specified above.
 * @example
 * formatShortSlashDate('2022-07-25T14:30:00.000Z') // '25/07/2022, 14:30'
 * formatShortSlashDate('2022-07-25T14:30:00.000Z', DateFormatString.US_DATE_SLASH) // '25/07/2022'
 */
export function formatShortSlashDate(
  isoDateString: number | string,
  dateFormatString?: DateFormatString,
) {
  const date = parseUtcDate(isoDateString);

  if (!isValid(date)) {
    return "-";
  }

  const pattern = isTruthy(dateFormatString)
    ? dateFormatString
    : DateFormatString.US_DATE_SLASH;

  return format(
    date,
    pattern,
    /[hHmsa]/.test(pattern) ? undefined : { in: UTC },
  );
}

/**
 * Formats a given date into a short, human-readable format, with months.
 * The format will be "MM/yyyy".
 *
 * @param date - The date to format.
 * @returns A human-readable date string in the format specified above.
 * @example
 * formatDateShortWithMonths(new Date('2022-07-25T14:30:00.000Z')) // '07/2022'
 */
export function formatDateShortWithMonths(date: Date) {
  if (!isValid(date)) {
    return "-";
  }

  return format(date, "MM/yyyy", { in: UTC });
}
export function formatDateOnlyMonths(date: Date) {
  if (!isValid(date)) {
    return "-";
  }

  return format(date, "MMM", { in: UTC });
}

export const useFormattedPreviousDate = (date: string | null) => {
  if (isFalsy(date)) {
    return null;
  }

  const datePrepared = parseUtcDate(date);

  if (!isValid(datePrepared)) {
    return null;
  }

  if (isToday(datePrepared)) {
    return m.dates_todayFormatted({
      date: format(datePrepared, "hh:mm a"),
    });
  }

  if (isYesterday(datePrepared)) {
    return m.dates_yesterdayFormatted({
      date: format(datePrepared, "hh:mm a"),
    });
  }

  return format(datePrepared, DateFormatString.US_WITH_TIME_AM_PM_DOT);
};

/**
 * Formats a date into either a relative time string (for past dates) or a formatted date string (for future dates)
 *
 * @param {Date} date - The date to format
 * @returns {string} For past dates, returns relative time (e.g. "2 days ago")
 *                   For today or future dates, returns formatted date (e.g. "25/12/2024")
 *
 * @example
 * // Past date (assuming today is 2024-12-11)
 * formatDueDate(new Date('2024-12-09')) // returns "2 days ago"
 *
 * // Future date
 * formatDueDate(new Date('2024-12-25')) // returns "25/12/2024"
 */
export const formatDueDate = (date: Date) => {
  const today = startOfToday();

  if (isBefore(date, today)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }

  return format(date, DateFormatString.US_DATE_SLASH, { in: UTC });
};

/**
 * Checks if a given date falls within a specified date range.
 *
 * @param {Date | string | undefined} date - The date to check.
 * @param {Date | string | undefined} fromDate - The start of the date range. If only this is provided, only this date is selected.
 * @param {Date | string | undefined} toDate - The end of the date range. If missing, defaults to `fromDate` to select only that day.
 * @returns {boolean} - `true` if the date is within the range, otherwise `false`.
 *
 * @example
 * // Returns true (date is within the range)
 * isDateInRange("2024-03-12", "2024-03-10", "2024-03-15");
 *
 * @example
 * // Returns false (date is before range)
 * isDateInRange("2024-03-09", "2024-03-10", "2024-03-15");
 *
 * @example
 * // Returns true (only this day is selected)
 * isDateInRange("2024-03-12", "2024-03-12");
 *
 * @example
 * // Returns false (only this day is selected, but the date does not match)
 * isDateInRange("2024-03-13", "2024-03-12");
 *
 * @example
 * // Returns true (from and to are equal, only that day is selected)
 * isDateInRange("2024-03-12", "2024-03-12", "2024-03-12");
 */
export const isDateInRange = (
  date: Date | string | undefined,
  fromDate?: Date | string,
  toDate?: Date | string,
): boolean => {
  if (!date) return true; // Ensure date is provided

  if (!fromDate && !toDate) return true;

  const dateObj = new Date(date);
  const fromDateObj = fromDate ? new Date(fromDate) : undefined;
  // Default to fromDate if toDate is missing
  const toDateObj = toDate ? new Date(toDate) : fromDateObj;

  // Normalize time to ensure we compare only the date part
  const dateStart = startOfDay(dateObj).getTime();
  const fromStart = fromDateObj ? startOfDay(fromDateObj).getTime() : undefined;
  const toEnd = toDateObj ? endOfDay(toDateObj).getTime() : undefined;

  // If both dates are the same, ensure only that day is selected
  if (fromStart && toEnd && fromStart === toEnd) {
    return dateStart === fromStart;
  }

  return (
    fromStart !== undefined &&
    toEnd !== undefined &&
    dateStart >= fromStart &&
    dateStart <= toEnd
  );
};

/**
 * Takes two optional Date parameters and returns an object with the
 * corresponding ISOString values for 'from' and 'to' properties. If either
 * of the parameters is missing or undefined, returns an empty object.
 *
 * @param fromDate - The start date of the range.
 * @param toDate - The end date of the range.
 * @returns An object with 'from' and 'to' properties containing the
 * ISOString values of the given dates.
 */
export const handleToDateAndFromDate = (
  fromDate?: Date | number | string | null,
  toDate?: Date | number | string | null,
): { from?: string; to?: string } => {
  const toISOIfValid = (
    input?: Date | number | string | null,
  ): string | undefined => {
    if (input === undefined || input === null || input === "") {
      return undefined;
    }

    const date = input instanceof Date ? input : new Date(input);

    if (!isValid(date)) {
      return undefined;
    }

    return date.toISOString();
  };

  const from = toISOIfValid(fromDate);
  const to = toISOIfValid(toDate);

  if (from && to) {
    return { from, to };
  }

  return {};
};
/**
 * Formats a given date string into a short, human-readable format, with year.
 * The format will be "yyyy".
 *
 * @param dateString - The date string to format.
 * @returns A human-readable date string in the format specified above.
 * @example
 * formatDateYear("2022-07-25T14:30:00.000Z") // returns "2022"
 */
export const formatDateYear = (dateString?: string) => {
  if (isFalsy(dateString)) {
    return;
  }
  const date = parseUtcDate(dateString);

  if (!isValid(date)) {
    return "-";
  }

  return format(date, "yyyy", { in: UTC });
};

/**
 * Formats a date string based on the selected format from DateFormatString enum.
 *
 * @param dateString - The ISO date string, timestamp, or Date object to format.
 * @param formatPattern - The selected format pattern from DateFormatString.
 * @returns The formatted date string or '-' if the date is invalid.
 *
 * @example
 * formatDateByPattern('2022-07-25T14:30:00.000Z', DateFormatString.UK_DATE_SLASH)
 * // Output: '25/07/2022'
 *
 * formatDateByPattern(new Date(), DateFormatString.US_WITH_TIME_AM_PM_DOT)
 * // Output: '07.25.2022 10:30 AM'
 */
export const formatDateByPattern = (
  dateString: Date | number | string,
  formatPattern: DateFormatString,
): string => {
  if (isFalsy(dateString)) {
    return "-";
  }

  const date = parseUtcDate(dateString);

  if (!isValid(date)) {
    return "-";
  }

  // Ensure "hh" (12-hour format) is converted to "HH" (24-hour format) if "a" is NOT present
  const adjustedPattern =
    formatPattern.includes("hh") && !formatPattern.includes("a")
      ? formatPattern.replace("hh", "HH") // Convert to 24-hour format
      : formatPattern;

  const hasTime = /[hHmsa]/.test(formatPattern);

  return format(date, adjustedPattern, hasTime ? undefined : { in: UTC });
};

/**
 * Formats a UTC date string/object if it represents a future date
 * (or optionally today) compared to the current date in UTC.
 *
 * @param date The date to check (Date object, ISO string, null, or undefined). Expected to be in UTC.
 * @param options.includeToday If true, considers the current UTC date as "future". Defaults to true.
 * @returns The formatted date string (MM/DD/YYYY, based on formatShortSlashDate) if it's in the future (or today, if specified) relative to UTC, otherwise null.
 */
export const formatDateIfFutureInUTC = (
  date: Date | string | null | undefined,
  options: {
    includeToday?: boolean;
  } = {},
): string | null => {
  if (isFalsy(date)) {
    return null;
  }

  const { includeToday = true } = options;
  const asUTC = parseUtcDate(date);

  if (!isValid(asUTC)) {
    console.warn(
      `formatDateIfFutureInUTC: Received invalid date input: ${String(date)}`,
    );

    return null;
  }

  const inputTimestampUTC = asUTC.getTime();
  const nowUTC = new Date();

  const startOfTodayUTC = Date.UTC(
    nowUTC.getUTCFullYear(),
    nowUTC.getUTCMonth(),
    nowUTC.getUTCDate(),
  );

  const thresholdTimestampUTC = includeToday
    ? startOfTodayUTC
    : Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate() + 1,
      );

  if (inputTimestampUTC >= thresholdTimestampUTC) {
    return convertISO8601ToUSSlashFormatDate(asUTC.toISOString());
  }

  return null;
};

/**
 * Converts a date string from YYYY-MM-DD format to MM/DD/YYYY format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date string in MM/DD/YYYY format
 */
export function convertISO8601ToUSSlashFormatDate(
  dateString: string | null | undefined,
): string {
  if (!dateString || typeof dateString !== "string") {
    return "-";
  }

  // Handle full ISO strings (with time)
  if (dateString.includes("T")) {
    // Extract just the date part (before the T)
    const datePart = dateString.split("T")[0];
    const parts = datePart.split("-");

    if (parts.length !== 3) {
      return "-";
    }

    const [year, month, day] = parts;

    return `${month}/${day}/${year}`;
  }

  // Handle date-only strings (YYYY-MM-DD)
  const parts = dateString.split("-");

  if (parts.length !== 3) {
    return "-";
  }

  const [year, month, day] = parts;

  return `${month}/${day}/${year}`;
}

/**
 * Formats a date string to "14 November 2025 at 4:12 PM" format
 * @param dateString - The ISO date string to format
 * @returns Formatted string (e.g., "14 November 2025 at 4:12 PM") or "-" if invalid
 * @example
 * formatDateWithTime("2025-11-14T16:12:00Z") // returns "14 November 2025 at 4:12 PM"
 */
export const formatDateWithTime = (dateString: string | undefined): string => {
  if (!dateString) return "-";

  const date = parseUtcDate(dateString);

  if (!isValid(date)) return "-";

  const day = date.getDate();
  const month = format(date, "MMMM");
  const year = date.getFullYear();
  const time = format(date, "h:mm a");

  return m.common_dateWithTime({ day, month, year, time });
};

/**
 * Helper function to format time difference between two timestamps
 * @param fromTimestamp - Start timestamp in milliseconds
 * @param toTimestamp - End timestamp in milliseconds
 * @returns Formatted string (e.g., "2d 12h 30m", "5h 45m", "30m")
 */
export const formatTimeDifference = (
  fromTimestamp: number,
  toTimestamp: number,
): string => {
  const diffMs = Math.abs(toTimestamp - fromTimestamp);

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);

  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  const parts = [`${days}d`, `${hours}h`, `${minutes}m`].filter(
    (str) => !str.startsWith("0"),
  );

  return parts.join(" ");
};

/**
 * Converts UTC timestamp to countdown format showing remaining SLA time or time since breach
 * @param createdAt - UTC timestamp when the item was created
 * @param slaHours - SLA value in hours (e.g., 24 for 24 hours)
 * @param slaBreachedTimestamp - UTC timestamp when SLA was breached, or null if not breached
 * @returns Formatted string showing remaining time (e.g., "2d 12h 30m") if not breached,
 *          or time since breach (e.g., "-1d 5h 20m") if breached
 */
export function formatSLATimer(
  createdAt: string,
  slaHours: number,
  slaBreachedTimestamp?: string | null,
): string {
  const nowUTC = Date.now();

  // If SLA was breached, calculate time since breach
  if (slaBreachedTimestamp && isValid(parseUtcDate(slaBreachedTimestamp))) {
    const breachedTimestampUTC = parseUtcDate(slaBreachedTimestamp).getTime();

    return `-${formatTimeDifference(breachedTimestampUTC, nowUTC)}`;
  }

  // Calculate remaining SLA time (or time past deadline)
  const createdAtUTC = parseUtcDate(createdAt).getTime();
  const slaDeadlineUTC = createdAtUTC + slaHours * 60 * 60 * 1000; // Add SLA hours in milliseconds

  return formatTimeDifference(nowUTC, slaDeadlineUTC);
}

export const formatTimeAgo = (
  isoDate: string | null | undefined,
): string | null => {
  if (!isoDate) return null;

  const date = new Date(isoDate);

  if (!isValid(date)) return null;

  return formatDistanceToNow(date, { addSuffix: true });
};
